import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Slider,
  Typography,
  Tooltip,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import { formatDateTime } from "../../utils/locationAnalytics";

/**
 * Scrubber + playback controls for replaying a user's movement history.
 *
 * Props:
 *   - points            : sorted point array
 *   - index             : current playhead index
 *   - onIndexChange     : (index) => void
 *   - isPlaying         : boolean
 *   - onPlayPause       : () => void
 *   - playbackSpeed     : number (how many indices per tick — controlled externally)
 */
export const MovementTimeline = ({
  points = [],
  index = 0,
  onIndexChange,
  isPlaying,
  onPlayPause,
  stepMs = 140,
}) => {
  // Auto-advance the playhead one index per tick while playing.
  // The parent passes `onIndexChange` as a functional updater so this effect
  // never needs to read the current index from closure.
  useEffect(() => {
    if (!isPlaying || points.length === 0) return undefined;
    const id = setInterval(() => {
      onIndexChange((current) => {
        const next = current + 1;
        if (next >= points.length) return points.length - 1;
        return next;
      });
    }, stepMs);
    return () => clearInterval(id);
  }, [isPlaying, points.length, onIndexChange, stepMs]);

  if (points.length === 0) return null;

  const current = points[Math.max(0, Math.min(index, points.length - 1))];

  const handleSlider = (_event, value) => {
    if (typeof value === "number") onIndexChange(() => value);
  };

  const atStart = index <= 0;
  const atEnd = index >= points.length - 1;

  return (
    <Card>
      <CardContent sx={{ py: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1.5,
          }}
        >
          <Tooltip title="Till början">
            <span>
              <IconButton
                size="small"
                disabled={atStart}
                onClick={() => onIndexChange(() => 0)}
              >
                <SkipPreviousRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isPlaying ? "Pausa uppspelning" : "Spela upp rörelse"}>
            <IconButton
              onClick={onPlayPause}
              sx={{
                bgcolor: "#8a1125",
                color: "#ffffff",
                "&:hover": { bgcolor: "#6b0d1d" },
              }}
            >
              {isPlaying ? (
                <PauseRoundedIcon />
              ) : (
                <PlayArrowRoundedIcon />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Till slutet">
            <span>
              <IconButton
                size="small"
                disabled={atEnd}
                onClick={() => onIndexChange(() => points.length - 1)}
              >
                <SkipNextRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              color: "#10324a",
              letterSpacing: "0.02em",
            }}
          >
            {formatDateTime(current.recorded_at)}
          </Typography>
        </Box>

        <Slider
          value={index}
          min={0}
          max={Math.max(0, points.length - 1)}
          step={1}
          onChange={handleSlider}
          valueLabelDisplay="off"
          sx={{
            color: "#8a1125",
            height: 6,
            "& .MuiSlider-rail": {
              background:
                "linear-gradient(90deg, rgba(138,17,37,0.12), rgba(16,50,74,0.18))",
              opacity: 1,
            },
            "& .MuiSlider-track": {
              background: "linear-gradient(90deg, #ff3b4e, #8a1125)",
              border: 0,
            },
            "& .MuiSlider-thumb": {
              width: 18,
              height: 18,
              background: "#ffffff",
              border: "3px solid #8a1125",
              boxShadow: "0 4px 12px rgba(138,17,37,0.35)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow:
                  "0 0 0 8px rgba(138,17,37,0.16), 0 4px 12px rgba(138,17,37,0.35)",
              },
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 1,
            color: "#7a8294",
            fontSize: 11,
            fontFamily: '"DM Sans", system-ui, sans-serif',
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>{formatDateTime(points[0].recorded_at)}</span>
          <span>
            {index + 1} / {points.length}
          </span>
          <span>{formatDateTime(points[points.length - 1].recorded_at)}</span>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MovementTimeline;
